import qs from "query-string"

import { isDevelopment } from "utils/environment"
import areAttributesValid from "./../utils/areAttributesValid"
import EVENTS from "./../utils/events"
import logToConsole from "./../utils/logToConsole"
import { removeEmptyKeys } from "./../utils/removeEmptyKeys"

const DEFAULT_CONFIG = {
  app: "companion",
  isEnabled: false,
  shouldTrack: !isDevelopment(),
  superAttributes: {},
  eventUserAttributes: {},
}

class Analytics {
  constructor(config) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config }
    this._app = finalConfig.app
    this._environment = finalConfig.environment
    this._eventUserAttributes = finalConfig.eventUserAttributes
    this._superAttributes = finalConfig.superAttributes
    this._shouldTrack = finalConfig.shouldTrack
    this._platform = "web"
    this._isLoggedIn = true
    this._isEnabled = Boolean(finalConfig.isEnabled)

    this._initialiseDataLayer()
    this._enhancePublicMethods()
  }

  setEnabled(isEnabled) {
    this._isEnabled = isEnabled
  }

  setLoggedIn(isLoggedIn) {
    this._isLoggedIn = isLoggedIn
  }

  setApp(app) {
    this._app = app
  }

  setEventUserAttributes(userAttributes = {}) {
    const cleanedUserAttributes = removeEmptyKeys(userAttributes)
    if (!areAttributesValid(EVENTS.setUserAttributes, cleanedUserAttributes)) {
      return
    }

    this._eventUserAttributes = cleanedUserAttributes
  }

  setSuperAttributes(superAttributes = {}) {
    this._superAttributes = {
      ...this._superAttributes,
      ...superAttributes,
    }
  }

  resetSuperAttributes() {
    this._superAttributes = {}
  }

  createClone(config = {}) {
    const clone = new Analytics({
      app: this._app,
      environment: this._environment,
      shouldTrack: this._shouldTrack,
      superAttributes: this._superAttributes,
      eventUserAttributes: this._eventUserAttributes,
    })
    clone.setLoggedIn(this._isLoggedIn)
    clone.setSuperAttributes(config.superAttributes || {})
    return clone
  }

  identify(userAnalyticsId) {
    this._trackEvent(EVENTS.identify, { user_analytics_id: userAnalyticsId })
    this.setLoggedIn(true)
  }

  setUserAttributes(attributes) {
    if (!this._isLoggedIn) {
      logToConsole("error", "User not identified yet to set attributes")
      return
    }

    this._trackEvent(EVENTS.setUserAttributes, attributes)
  }

  pageview(attributes = {}) {
    const finalAttributes = this._createPageViewAttributes(attributes)
    this._trackEvent(EVENTS.pageview, finalAttributes)
  }

  click(attributes) {
    this._trackEvent(EVENTS.click, attributes)
  }

  view(attributes) {
    this._trackEvent(EVENTS.view, attributes)
  }

  log(attributes) {
    this._trackEvent(EVENTS.log, attributes)
  }

  get app() {
    return this._app
  }

  get environment() {
    return this._environment
  }

  get isEnabled() {
    return this._isEnabled
  }

  get platform() {
    return this._platform
  }

  get superAttributes() {
    return this._superAttributes
  }

  // ---- Private Methods ----
  _enhancePublicMethods() {
    const methodsToEnhance = [
      "setApp",
      "setSuperAttributes",
      "resetSuperAttributes",
      "createClone",
      "identify",
      "setUserAttributes",
      "pageview",
      "click",
      "view",
      "log",
    ]
    methodsToEnhance.forEach((methodName) => {
      this[methodName] = this._supressErrorsFromMethod(this[methodName])
    })
  }

  _supressErrorsFromMethod(fn) {
    const context = this
    return function (...args) {
      try {
        return fn.apply(context, args)
      } catch (error) {
        logToConsole("error", error)
        return null
      }
    }
  }

  _initialiseDataLayer() {
    if (typeof window === "undefined") return

    window.dataLayer ||= []
  }

  _createPageViewAttributes(attributes) {
    if (typeof window === "undefined") return

    const { title } = window.document
    const { href, pathname, search } = window.location
    return {
      page_title: title,
      page_path: pathname,
      page_url: href,
      query_params: qs.parse(search),
      ...attributes,
    }
  }

  _createEventPayload(event, _attributes) {
    const { custom: customAttributes, ...attributes } = _attributes
    return {
      event,
      platform: this.platform,
      environment_id: this.environment,
      pathname: window?.location?.pathname,
      app: this.app,
      attributes,
      user_attributes: this._eventUserAttributes,
      custom_attributes: {
        ...this.superAttributes,
        ...customAttributes,
      },
    }
  }

  _trackEvent(event, _attributes) {
    if (!this.isEnabled) return

    const attributes = removeEmptyKeys(_attributes)
    if (!areAttributesValid(event, attributes)) return

    const eventPayload = this._createEventPayload(event, attributes)

    if (this._shouldTrack) {
      this._pushToDataLayer(eventPayload)
    } else {
      this._logEvent(eventPayload)
    }
  }

  _pushToDataLayer(payload) {
    if (typeof window === "undefined") return

    window.dataLayer.push({
      _clear: true,
      ...payload,
    })
  }

  _logEvent(payload) {
    logToConsole("info", `"${payload.event}" event has been received with below payload: `, payload)
  }
}

export default Analytics
